import axios, { AxiosError } from 'axios';

import type { ApiError } from '@/types/api.types';

type TokenGetter = () => string | null;
type LogoutHandler = () => void;

// Injected by the auth store after init to avoid circular imports.
// Uses idToken (not accessToken) because API Gateway JWT authorizer validates
// the "aud" claim, which Cognito only includes in the id token.
let getBearerToken: TokenGetter = () => null;
let handleUnauthorized: LogoutHandler = () => undefined;

export function setAuthInterceptors(
  tokenGetter: TokenGetter,
  logoutHandler: LogoutHandler,
): void {
  getBearerToken = tokenGetter;
  handleUnauthorized = logoutHandler;
}

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getBearerToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const apiError: ApiError = {
      message: 'An unexpected error occurred',
      code: 'UNKNOWN_ERROR',
      statusCode: error.response?.status ?? 0,
    };

    if (error.response?.data && typeof error.response.data === 'object') {
      const data = error.response.data as Record<string, unknown>;
      // New envelope: { success: false, error: { code, message, errors?, requestId } }
      if (data['error'] && typeof data['error'] === 'object') {
        const err = data['error'] as Record<string, unknown>;
        if (typeof err['message'] === 'string') apiError.message = err['message'];
        if (typeof err['code'] === 'string') apiError.code = err['code'];
        if (typeof err['requestId'] === 'string') apiError.requestId = err['requestId'];
        if (Array.isArray(err['errors'])) apiError.errors = err['errors'] as import('@/types/api.types').ApiFieldError[];
        if (err['fields'] && typeof err['fields'] === 'object' && !Array.isArray(err['fields'])) {
          apiError.fields = err['fields'] as Record<string, string>;
        }
      } else {
        if (typeof data['message'] === 'string') apiError.message = data['message'];
        if (typeof data['code'] === 'string') apiError.code = data['code'];
      }
    }

    if (error.response?.status === 401) {
      handleUnauthorized();
      window.location.href = '/login';
    }

    return Promise.reject(apiError);
  },
);
